import { FamilyTreeNode } from '@business/interfaces/family-tree/types';
import React, { createContext, useCallback, useContext, useState } from 'react';

export interface ContextMenuState {
  isOpen: boolean;
  position: { x: number; y: number } | null;
  nodeId: string | null;
}

export interface SpousePopoverState {
  isOpen: boolean;
  personId: string | null;
  anchor: { x: number; y: number } | null;
}

interface FamilyTreeContextType {
  referenceId: string | null;
  subscriptionOwnerId: string | null;
  contextMenu: ContextMenuState;
  profileViewId: string | null;
  selectedNodeIds: string[];
  subtreePath: FamilyTreeNode[];
  nodes: FamilyTreeNode[];
  hiddenRootMap: Record<string, boolean>;
  setReferenceId: (id: string) => void;
  setSubscriptionOwnerId: (id: string) => void;
  setContextMenu: (state: ContextMenuState) => void;
  setSubtreePath: (path: FamilyTreeNode[]) => void;
  setNodes: React.Dispatch<React.SetStateAction<FamilyTreeNode[]>>;
  handleToggleNodeSelection: (id: string) => void;
  handleSetReferenceId: (id: string | null) => void;
  setProfileViewId: React.Dispatch<React.SetStateAction<string | null>>;
  setHiddenRootMap: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
}

const FamilyTreeContext = createContext<FamilyTreeContextType | undefined>(
  undefined
);

export const FamilyTreeProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [nodes, setNodes] = useState<FamilyTreeNode[]>([]);
  const [referenceId, setReferenceId] = useState<string | null>(null);
  const [subscriptionOwnerId, setSubscriptionOwnerId] = useState<string | null>(
    null
  );
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    isOpen: false,
    position: { x: 0, y: 0 },
    nodeId: null
  });

  const [hiddenRootMap, setHiddenRootMap] = useState<Record<string, boolean>>(
    {}
  );
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [subtreePath, setSubtreePath] = useState<FamilyTreeNode[]>([]);
  const [profileViewId, setProfileViewId] = useState<string | null>(null);

  const handleToggleNodeSelection = useCallback((id: string) => {
    setSelectedNodeIds(prev =>
      prev.includes(id) ? prev.filter(nid => nid !== id) : [...prev, id]
    );
  }, []);

  /**
   * Handle navigation into a subtree (when apple icon is clicked)
   * This is called from FamilyTreeView via setReferenceId prop
   */
  const handleSetReferenceId = useCallback(
    (id: string | null) => {
      if (id === null) {
        // Going back to main tree
        setReferenceId(null);
        setSubtreePath(prev => {
          if (prev.length > 0) return [prev[0]];

          const currentOwner = nodes.find(n => n.isOwner);
          return currentOwner ? [currentOwner] : [];
        });
      } else {
        setSubtreePath(prev => {
          const owner = nodes.find(n => n.isOwner);
          let base = prev.length > 0 ? prev : owner ? [owner] : [];
          const existingIdx = base.findIndex(n => n.id === id);
          if (existingIdx >= 0) {
            return base.slice(0, existingIdx + 1);
          }
          const nextNode = nodes.find(n => n.id === id);
          if (nextNode) {
            const newPath = [...base, nextNode];
            // Cap at 3 items: keep root (index 0) + last 2 entries
            if (newPath.length > 3) {
              return [newPath[0], ...newPath.slice(-2)];
            }
            return newPath;
          }
          return base;
        });
        const owner = nodes.find(n => n.isOwner);
        if (owner && id === owner.id) {
          setReferenceId(null);
        } else {
          setReferenceId(id);
        }
      }
    },
    [nodes]
  );

  return (
    <FamilyTreeContext.Provider
      value={{
        referenceId,
        subscriptionOwnerId,
        contextMenu,
        hiddenRootMap,
        profileViewId,
        selectedNodeIds,
        subtreePath,
        nodes,
        setReferenceId,
        setSubscriptionOwnerId,
        setContextMenu,
        setHiddenRootMap,
        setSubtreePath,
        setNodes,
        handleToggleNodeSelection,
        handleSetReferenceId,
        setProfileViewId
      }}>
      {children}
    </FamilyTreeContext.Provider>
  );
};

export function useFamilyTreeContext() {
  const context = useContext(FamilyTreeContext);
  if (!context) {
    throw new Error(
      'useFamilyTreeContext must be used within a FamilyTreeProvider'
    );
  }
  return context;
}
